import React from 'react';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { useReporter } from '../../reporter';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { PillText } from '../PillText';
import { headingLinkClicked } from './events';
import {
  HeadingStyled,
  HeadingWithBadge,
  StyledLink,
  LinkContainer,
  HeadingContainer,
} from './styles';

export const Heading = ({
  headingText,
  headingLevel,
  position = 'left',
  mobilePosition,
  typography = 'headingMastiff',
  backgroundColor = 'background.lightest',
  verticalPadding = 0,
  horizontalPadding = 0,
  badge = '',
  badgeVariant = 'dark',
  badgeIcon,
  badgeIconAlignment = 'left',
  link,
  fontColor = 'text.primary',
}) => {
  const reporter = useReporter();
  const { formattedAmount } = useShopifyOfferContext();

  typography =
    typeof typography === 'string'
      ? { desktop: typography, mobile: typography }
      : typography;

  return (
    <HeadingContainer
      $position={position}
      $mobilePosition={mobilePosition || position}
      $bgColor={backgroundColor}
      $horizontalPadding={horizontalPadding}
      $verticalPadding={verticalPadding}
    >
      <HeadingWithBadge
        $position={position}
        $mobilePosition={mobilePosition || position}
      >
        <HeadingStyled
          $bgColor={backgroundColor}
          as={headingLevel}
          $typography={typography}
          $position={position}
          $mobilePosition={mobilePosition || position}
          $fontColor={fontColor}
        >
          {interpolate(headingText, { offer: formattedAmount })}
        </HeadingStyled>
        {badge && (
          <>
            &nbsp;
            <PillText
              text={badge}
              variant={badgeVariant}
              icon={badgeIcon}
              iconAlignment={badgeIconAlignment}
            />
          </>
        )}

        {link && (
          <LinkContainer>
            <StyledLink
              $fontColor={fontColor}
              $typography={typography}
              href={link.target}
              onClick={() =>
                reporter.tag(headingLinkClicked(link.text, headingText))
              }
            >
              {link.text}
            </StyledLink>
          </LinkContainer>
        )}
      </HeadingWithBadge>
    </HeadingContainer>
  );
};

Heading.displayName = 'Heading';

Heading.propTypes = {
  typography: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      desktop: PropTypes.string,
      mobile: PropTypes.string,
    }),
  ]),
  horizontalPadding: PropTypes.number,
  verticalPadding: PropTypes.number,
  headingText: PropTypes.string.isRequired,
  headingLevel: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'])
    .isRequired,
  position: PropTypes.oneOf(['left', 'right', 'center']),
  mobilePosition: PropTypes.oneOf(['left', 'right', 'center']),
  backgroundColor: PropTypes.string,
  badge: PropTypes.string,
  badgeVariant: PropTypes.string,
  badgeIcon: PropTypes.shape({
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    alt: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  badgeIconAlignment: PropTypes.oneOf(['left', 'right']),
  link: PropTypes.shape({
    text: PropTypes.string.isRequired,
    target: PropTypes.string.isRequired,
  }),
  fontColor: PropTypes.string,
};
